import {Clock} from 'lucide-react';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {useTheme} from 'styled-components';
import {useTranslation} from 'react-i18next';
import {Offer, OFFER_TEMPLATES, SelectOption} from '@chekin/core';
import {RadioSelect} from '@chekin/guestapp-ui-kit';
import {usePaymentSettings} from 'hooks';

import styles from './TimeBasedCounter.module.css';

type TimeBasedCounterProps = {
  items: Offer['items'];
  template?: string;
  onItemChange: (itemId: string, value: number) => void;
};

const formatPrice = (currencySymbol: string, price: number) =>
  `${currencySymbol}${price.toFixed(2)}`;

export const TimeBasedCounter = ({
  items,
  template,
  onItemChange,
}: TimeBasedCounterProps) => {
  const {t} = useTranslation();
  const {currencySymbol} = usePaymentSettings();
  const theme = useTheme();

  const itemPrices = useMemo(() => items || [], [items]);

  const isEarlyCheckIn = template === OFFER_TEMPLATES.checkIn;
  const isLateCheckOut = template === OFFER_TEMPLATES.checkOut;

  let subtitleKey: string | null = null;
  if (isEarlyCheckIn) {
    subtitleKey = t('standard_checkin_time', {time: '16:00'});
  } else if (isLateCheckOut) {
    subtitleKey = t('standard_checkout_time', {time: '10:00'});
  }

  const [selected, setSelected] = useState<
    SelectOption<{rightContent?: string}> | undefined
  >();

  const selectItem = useCallback(
    (itemId: string) => {
      itemPrices.forEach(item => {
        onItemChange(item.id, item.id === itemId ? 1 : 0);
      });
    },
    [itemPrices, onItemChange],
  );

  const handleChange = (option: SelectOption<{rightContent?: string}>) => {
    setSelected(option);
    selectItem(option.value);
  };

  const options = useMemo(
    () =>
      itemPrices.map(item => ({
        value: item.id,
        label: (item.name || '').split(':').splice(0, 2).join(':'),
        data: {
          rightContent: formatPrice(currencySymbol, Number(item.price)),
        },
      })),
    [currencySymbol, itemPrices],
  );

  useEffect(() => {
    if (options[0] && !selected) {
      setSelected(options[0]);
      selectItem(options[0].value);
    }
  }, [selected, options, selectItem]);

  return (
    <>
      <div className={styles.titleContainer}>
        <Clock
          size={24}
          color={theme.hasTemplate ? theme.colors.branding.icons : '#777E91'}
        />
        <div className={styles.titleContent}>
          <p className={styles.titleHeading}>{t('select_time')}</p>
          {subtitleKey && <p className={styles.titleSubtitle}>{subtitleKey}</p>}
        </div>
      </div>

      <RadioSelect value={selected} options={options} onChange={handleChange} />
    </>
  );
};
